import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button } from '@storable/canaveral-components';
import AccordionEditItem from './AccordionEditItem';
import styles from './AccordionEdit.scss';
import PlaceholderIcon from '../../../../../modules/react/components/stateless/PlaceholderIcon';

const propTypes = {
  accordions: PropTypes.arrayOf(PropTypes.object),
  isEditing: PropTypes.bool.isRequired,
  deleteAccordion: PropTypes.func.isRequired,
  editAccordion: PropTypes.func.isRequired,
  defaultStateChange: PropTypes.func.isRequired,
  onFinishEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  invalidAccordion: PropTypes.string,
};

const defaultProps = {
  accordions: [],
  invalidAccordion: null,
};

const AccordionEdit = ({ accordions, ...props }) => {
  const { isEditing, deleteAccordion, editAccordion,
    defaultStateChange, onFinishEdit, onCancelEdit,
    addAccordion, defaultOpenChange,
    invalidTitles, invalidBodies,
    reOrderAccordionsHandler, draggingHandler, isDragging, panelCloseStatesEditMode,
    togglePanelEditMode } = props;

  const contentRef = useRef(null);

  const scrollIntoView = () => {
    contentRef?.current?.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  };

  const finishEdit = () => {
    onFinishEdit();
    scrollIntoView();
  };

  const cancelEdit = () => {
    onCancelEdit();
    scrollIntoView();
  };

  const saveTextContent = (accordionItemId, contentType, content) => {
    editAccordion(accordionItemId, contentType, content);
  };

  const reOrderAccordions = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    reOrderAccordionsHandler(result);
  };
  // eslint-disable-next-line no-unused-vars
  const getItemStyle = (isSnapshotDragging, draggableStyle) => ({
    userSelect: 'none',
    ...draggableStyle,
  });

  // eslint-disable-next-line no-unused-vars
  const getListStyle = isDraggingOver => ({
    width: '100%',
  });

  const onDragStart = () => {
    draggingHandler(true);
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      draggingHandler(false);
      return;
    }
    reOrderAccordions(
      accordions,
      result.source.index,
      result.destination.index,
    );
    draggingHandler(false);
  };

  const isInvalidAccordion = invalidTitles.length > 0 || invalidBodies.length > 0;

  return (
    <div className={styles.accordionEditor} ref={contentRef}>
      <DragDropContext
        onBeforeDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={getListStyle(snapshot.isDraggingOver)}
            >
              {accordions.map((item, index) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {/* eslint-disable-next-line no-shadow */}
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={getItemStyle(
                        snapshot.isDragging,
                        provided.draggableProps.style,
                      )}
                    >
                      <AccordionEditItem
                        item={item}
                        accordions={accordions}
                        isEditing={isEditing}
                        deleteAccordion={deleteAccordion}
                        onToggle={togglePanelEditMode}
                        panelCloseStatesEditMode={panelCloseStatesEditMode}
                        saveTextContent={saveTextContent}
                        onSelectChange={defaultStateChange}
                        isTitleInvalid={invalidTitles.includes(item.id)}
                        isBodyInvalid={invalidBodies.includes(item.id)}
                        defaultOpenChange={defaultOpenChange}
                        isDragging={isDragging}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <div className={styles.addButtonContainer}>
        <Button type="button" onClick={addAccordion} className={styles.addButton}>
          <PlaceholderIcon.AddIcon
            width="14"
            height="14"
            fill="#686868"
          />
          <span className={styles.addItemText}>Add Accordion Item</span>
        </Button>
      </div>
      <div className={styles.errorTextContainer}>
        <p
          className={isInvalidAccordion ? styles.errorText : styles.errorTextHidden}
        >Accordion items cannot be left blank. Please complete all fields before saving.
        </p>
      </div>
      <div className={styles.buttonsContainer}>
        <Button
          onClick={cancelEdit}
          className={styles.cancelButton}
          context="neutral"
        >
          Cancel
        </Button>
        <Button
          disabled={isInvalidAccordion}
          onClick={finishEdit}
          className={styles.saveButton}
          context="positive"
        >
          Save
        </Button>
      </div>
    </div>
  );
};

AccordionEdit.propTypes = propTypes;
AccordionEdit.defaultProps = defaultProps;

export default AccordionEdit;
